"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import {
  LayoutDashboard, Github, Globe, ArrowRight, ArrowLeft, Loader2, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { LogoIcon } from "@/components/logo";

const STORAGE_KEY = "axlynq_onboarding_done";
const TOTAL_STEPS = 4;

interface OnboardingWizardProps {
  userName: string;
  orgName: string;
  orgSlug: string;
}

function ProgressBar({ step }: { step: number }) {
  return (
    <div className="absolute top-0 left-0 w-full h-1 bg-muted rounded-t-xl overflow-hidden">
      <div
        className="h-full bg-foreground transition-all duration-500 ease-out"
        style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
      />
    </div>
  );
}

export function OnboardingWizard({ userName, orgName, orgSlug }: OnboardingWizardProps) {
  const { toast } = useToast();
  const router = useRouter();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [workspaceName, setWorkspaceName] = useState(orgName);
  const [workspaceSlug, setWorkspaceSlug] = useState(orgSlug);
  const [usageType, setUsageType] = useState<"dashboard" | "github" | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [detectedName, setDetectedName] = useState("");

  useEffect(() => {
    const done = localStorage.getItem(STORAGE_KEY);
    if (!done) setVisible(true);
  }, []);

  useEffect(() => {
    if (!websiteUrl) { setDetectedName(""); return; }
    try {
      const url = websiteUrl.startsWith("http") ? websiteUrl : `https://${websiteUrl}`;
      const hostname = new URL(url).hostname.replace(/^www\./, "");
      const name = hostname.split(".")[0];
      setDetectedName(name.charAt(0).toUpperCase() + name.slice(1));
    } catch {
      setDetectedName("");
    }
  }, [websiteUrl]);

  const handleSlugify = (name: string) => {
    setWorkspaceName(name);
    setWorkspaceSlug(name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""));
  };

  const finish = async () => {
    setLoading(true);
    try {
      if (websiteUrl) {
        const url = websiteUrl.startsWith("http") ? websiteUrl : `https://${websiteUrl}`;
        const res = await fetch("/api/projects", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: detectedName || "My Project", url }),
        });
        if (res.ok) {
          const project = await res.json();
          localStorage.setItem(STORAGE_KEY, "1");
          setVisible(false);
          router.push(`/dashboard/projects/${project.id}`);
          return;
        }
      }
      localStorage.setItem(STORAGE_KEY, "1");
      setVisible(false);
      router.push("/dashboard/projects");
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative w-full max-w-lg mx-4 bg-card rounded-xl shadow-2xl overflow-hidden">
        <ProgressBar step={step} />

        <div className="p-8 pt-10 min-h-[320px] flex flex-col">
          {step === 1 && (
            <div className="flex flex-col items-center justify-center flex-1 text-center gap-3">
              <LogoIcon size={64} className="mb-2" />
              <h2 className="text-2xl font-bold text-foreground">Welcome to Axlynq</h2>
              <p className="text-muted-foreground font-medium">{userName}</p>
              <p className="text-muted-foreground text-sm max-w-xs">
                Let&apos;s set up your workspace. This will only take a minute.
              </p>
            </div>
          )}

          {step === 2 && (
            <div className="flex flex-col flex-1 gap-5">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-muted">
                    <LayoutDashboard className="h-6 w-6 text-foreground" />
                  </div>
                </div>
                <h2 className="text-xl font-bold text-foreground">Create your workspace</h2>
                <p className="text-sm text-muted-foreground mt-1">Your workspace is where your team collaborates on testing projects.</p>
              </div>
              <div className="space-y-2">
                <Label>Workspace name</Label>
                <Input
                  value={workspaceName}
                  onChange={(e) => handleSlugify(e.target.value)}
                  placeholder="My Company"
                  className=""
                />
              </div>
              <div className="space-y-2">
                <Label>Workspace slug</Label>
                <div className="flex items-center rounded-md border border-border overflow-hidden focus-within:ring-2 focus-within:ring-ring">
                  <span className="px-3 py-2 text-sm text-muted-foreground bg-muted border-r border-border select-none">/</span>
                  <input
                    value={workspaceSlug}
                    onChange={(e) => setWorkspaceSlug(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm outline-none"
                  />
                  {workspaceSlug && <Check className="h-4 w-4 text-green-500 mr-3" />}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="flex flex-col flex-1 gap-6">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-muted">
                    <Globe className="h-6 w-6 text-foreground" />
                  </div>
                </div>
                <h2 className="text-xl font-bold text-foreground">How do you want to use Axlynq?</h2>
                <p className="text-sm text-muted-foreground mt-1">You can always change this later from settings.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  {
                    key: "dashboard" as const,
                    icon: <LayoutDashboard className="h-7 w-7" />,
                    label: "Dashboard",
                    desc: "Manage tests and projects from the web dashboard.",
                  },
                  {
                    key: "github" as const,
                    icon: <Github className="h-7 w-7" />,
                    label: "GitHub",
                    desc: "Automate tests on every pull request with GitHub App integration.",
                  },
                ].map(({ key, icon, label, desc }) => (
                  <button
                    key={key}
                    onClick={() => setUsageType(key)}
                    className={cn(
                      "flex flex-col items-center gap-3 rounded-lg border-2 p-5 text-center transition-all",
                      usageType === key
                        ? "border-foreground bg-foreground/5"
                        : "border-border hover:border-foreground/40 bg-card"
                    )}
                  >
                    <div className={cn(usageType === key ? "text-foreground" : "text-muted-foreground")}>{icon}</div>
                    <div>
                      <p className="font-semibold text-foreground text-sm">{label}</p>
                      <p className="text-xs text-muted-foreground mt-1">{desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="flex flex-col flex-1 gap-5">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-muted">
                    <Globe className="h-6 w-6 text-foreground" />
                  </div>
                </div>
                <h2 className="text-xl font-bold text-foreground">What do you want to test?</h2>
                <p className="text-sm text-muted-foreground mt-1">Enter your website URL and we&apos;ll set up the project for you.</p>
              </div>
              <div className="space-y-2">
                <Label>Website URL</Label>
                <div className="flex items-center rounded-md border border-border overflow-hidden focus-within:ring-2 focus-within:ring-ring">
                  <Globe className="h-4 w-4 text-muted-foreground ml-3 shrink-0" />
                  <input
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="yourapp.com"
                    className="flex-1 px-3 py-2 text-sm outline-none"
                  />
                </div>
                {detectedName && (
                  <p className="text-xs text-muted-foreground">Detected: <span className="font-medium text-foreground">{detectedName}</span></p>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between mt-8 pt-4 border-t border-border">
            {step > 1 ? (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            ) : (
              <div />
            )}

            {step < TOTAL_STEPS ? (
              <Button
                onClick={() => setStep((s) => s + 1)}
                disabled={step === 3 && !usageType}
                className="bg-foreground hover:bg-foreground/90 text-background border-0 px-6"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={finish}
                disabled={loading}
                className="bg-foreground hover:bg-foreground/90 text-background border-0 px-6"
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {websiteUrl ? "Create project" : "Go to dashboard"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
